import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { FormattedMessage, defineMessages, injectIntl } from "react-intl"
import { useMutation } from "react-query"
import Select from "react-select"
import ActionButton from "../../common/button/actionbutton.component"
import LoadingSpinner from "../../common/spinner/loading.component"
import IntlProps from "../../state/helpers/IntlProps"
import { INTEGRATION_KEY } from "../../state/integration/Constants"
import ContactImportableService from "../../state/integration/ContactImportableService"
import ExternalSaleImportableService from "../../state/integration/ExternalSaleImportableService"
import Invoice from "../../state/invoice/models/invoice"
import Order from "../../state/order/Order"
import { PROPOSAL_TYPE } from "../../state/proposal/ProposalType"
import Quote from "../../state/quote/Quote"

const messages = defineMessages({
    noContactWarning: {
        id: "integration.hubspot.no_contact.warning",
        defaultMessage: "No contact is assigned to this HubSpot deal. Please assign at least one contact and come back.",
    },
})

type SelectedContact = {
    id: string,
    externalId: string
}

const NewDocumentComponent = (props: IntlProps) => {
    const [mainContact, setMainContact] = useState<SelectedContact | null>(null)
    const [dealId, setDealId] = useState<string>("")
    const [documentType, setDocumentType] = useState<PROPOSAL_TYPE>(PROPOSAL_TYPE.INVOICE)
    const [errorMessage, setErrorMessage] = useState<string>("")

    const { mutateAsync: createDocument, isLoading: isCreatingDocument } = useMutation(
        () => {
            return ExternalSaleImportableService.createNewDocumentWithExternalSaleID({
                externalId: dealId,
                integrationKey: INTEGRATION_KEY.HUBSPOT,
                documentType: documentType,
                externalContactId: mainContact?.externalId || "",
            })
        }, {
            onSuccess: (doc) => {
                const docId = doc.getId()
                if (doc instanceof Invoice) {
                    window.open(`${AppContext.getOptions().webCpqUrl}/invoices/${docId}/edit`, "_blank")
                } else if (doc instanceof Quote) {
                    window.open(`${AppContext.getOptions().webCpqUrl}/quotes/${docId}/edit`, "_blank")
                } else if (doc instanceof Order) {
                    window.open(`${AppContext.getOptions().webCpqUrl}/orders/${docId}/edit`, "_blank")
                }

                //giving enough time to elastic to index the newly created document
                setTimeout(() => {
                //this closes the modal in HubSpot
                    window.parent.postMessage(JSON.stringify({ "action": "DONE" }), "*")
                }, 500)
            },
            onError: (err: Error) => {
                setErrorMessage(err.message)
            }
        })

    const {
        mutateAsync: importContacts,
        isLoading: isImportingContacts,
        data: importedContacts,
    } = useMutation(
        (dealId: string) => {
            return ContactImportableService.importContactsFromExternalSale(INTEGRATION_KEY.HUBSPOT, dealId)
        }, {
            onSuccess: (contacts) => {
                if (contacts.length > 0) {
                    setMainContact({
                        id: contacts[0].getId(),
                        externalId: contacts[0].getExternalIDByIntegrationKey(INTEGRATION_KEY.HUBSPOT) || "",
                    })
                } else {
                    setErrorMessage(props.intl.formatMessage(messages.noContactWarning))
                }
            },
            onError: (err: Error) => {
                setErrorMessage(err.message)
            }
        })


    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search)
        const dealId = searchParams.get("dealId") || ""
        setDealId(dealId)
        const documentTypeValue = searchParams.get("documentType") || ""
        const documentTypeMapping = {
            [PROPOSAL_TYPE.QUOTE]: PROPOSAL_TYPE.QUOTE,
            [PROPOSAL_TYPE.INVOICE]: PROPOSAL_TYPE.INVOICE,
            [PROPOSAL_TYPE.ORDER]: PROPOSAL_TYPE.ORDER,
        }
        setDocumentType(documentTypeMapping[documentTypeValue])

        const abortController = new AbortController()

        async function importInitialContacts() {
            importContacts(dealId)
        }

        importInitialContacts()


        return () => {
            abortController.abort()
        }
    }, [])

    if (errorMessage) {
        return <div className="text-danger">
            {errorMessage}
        </div>
    }

    if (isImportingContacts) {
        return <div className="d-flex flex-column align-items-center">
            <LoadingSpinner />
            <FormattedMessage
                id="common.loading_contacts.label"
                defaultMessage="Loading your contacts..."
            />
        </div>
    }

    return <div className="">
        {(!isCreatingDocument) ?
            <>
                <label>
                    <FormattedMessage
                        id="hubspot.select_main_contact.label"
                        defaultMessage="Select the main contact"
                    />
                </label>
                <Select
                    name={"hubspot-contact-select"}
                    value={mainContact?.id || ""}
                    options={importedContacts?.map(contact => {
                        return {
                            value: contact.getId(),
                            externalId: contact.getExternalIDByIntegrationKey(INTEGRATION_KEY.HUBSPOT) || "",
                            label: contact.getName(),
                        }
                    })}
                    clearable={false}
                    searchable={false}
                    multi={false}
                    onChange={op => {
                        setMainContact({
                            id: op.value,
                            externalId: op.externalId,
                        })
                    }}
                />
                <div className="d-flex justify-content-end mt-4">
                    <ActionButton
                        type="button"
                        onClick={e => {
                            e.preventDefault()
                            createDocument()
                        }}
                        className="btn btn-primary"
                        isLoading={isCreatingDocument}
                        disabled={!mainContact}
                    >
                        <FormattedMessage
                            id="common.create_document.label"
                            defaultMessage="Create document"
                        />
                    </ActionButton>
                </div>
            </>
            :
            <div className="d-flex flex-column align-items-center">
                <LoadingSpinner />
                <FormattedMessage
                    id="common.create_document_loading.label"
                    defaultMessage="Creating your document..."
                />
            </div>
        }
    </div>
}
export default injectIntl(NewDocumentComponent)


// WEBPACK FOOTER //
// ./src/integration/hubspot/NewDocumentComponent.tsx